import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Modal, Select } from 'antd';
import { CheckOutlined, DownloadOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getInventaireConfig, getInventaire, getInventaireLignes,
  saveInventaireLigne, abandonnerInventaire,
} from '../../services/inventaireService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeStatusBadge, NdoeImage,
  NdoeStats, NdoeStatCard, NdoeError, NdoeLoading,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from '../rapports/exportCsv';
import useRefLists from '../rapports/useRefLists';
import CataloguePicker from '../journalTransactions/CataloguePicker';
import { money } from '../rapports/format';
import './inventaires.css';

const PAGE_SIZE = 100;

const LIGNE_STATUTS = [
  { value: '', label: 'Toutes les lignes' },
  { value: 'a_compter', label: 'À compter' },
  { value: 'comptee', label: 'Comptées' },
  { value: 'ecart', label: 'Avec écart' },
  { value: 'a_recompter', label: 'À recompter' },
  { value: 'validee', label: 'Validées' },
];

const ligneBadge = (l) => {
  if (l.statut_ligne === 'a_recompter') return <NdoeStatusBadge kind="custom" tone="warn" label="À recompter" />;
  if (l.statut_ligne === 'validee') return <NdoeStatusBadge kind="custom" tone="ok" label="Corrigée" />;
  if (l.statut_ligne === 'sans_ecart') return <NdoeStatusBadge kind="custom" tone="ok" label="Sans écart" />;
  if (l.statut_ligne === 'comptee') {
    return Number(l.ecart) === 0
      ? <NdoeStatusBadge kind="custom" tone="ok" label="Comptée · juste" />
      : <NdoeStatusBadge kind="custom" tone="danger" label={`Écart ${l.ecart > 0 ? '+' : ''}${l.ecart}`} />;
  }
  return <NdoeStatusBadge kind="custom" tone="neutral" label="À compter" />;
};

export default function InventaireSession() {
  const { id } = useParams();

  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [header, setHeader] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [meta, setMeta] = useState({ page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);

  const { openFiche, ficheElement } = useFicheArticle();
  const refs = useRefLists(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [brouillons, setBrouillons] = useState({});   // id_ligne → saisie en cours
  const [enregistrement, setEnregistrement] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const loadHeader = useCallback(async () => {
    const { data } = await getInventaire(id);
    setHeader(data);
  }, [id]);

  const loadLignes = useCallback(async () => {
    const { data } = await getInventaireLignes(id, { q: search, statut, page, page_size: PAGE_SIZE });
    setLignes(data.data || []);
    setMeta(data.meta || { page: 1, total: 0 });
  }, [id, search, statut, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }] = await Promise.all([getInventaireConfig(), loadHeader(), loadLignes()]);
      setCfg(c);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || "Impossible de charger l'inventaire.");
    } finally { setLoading(false); }
  }, [loadHeader, loadLignes]);

  useEffect(() => { load(); }, [load]);

  const enCours = header?.statut === 'en_cours';
  const peutValider = !!cfg?.peut_valider;
  const classifications = useMemo(
    () => (cfg?.classifications || []).map((c) => ({ value: c.code, label: c.label })),
    [cfg],
  );

  // --- Comptage d'une ligne -------------------------------------------------------
  const enregistrer = async (ligne, patch) => {
    setEnregistrement((s) => ({ ...s, [ligne.id_ligne]: true }));
    try {
      const { data } = await saveInventaireLigne(id, ligne.id_ligne, patch);
      const maj = data.ligne || {};
      setLignes((ls) => ls.map((l) => (l.id_ligne === ligne.id_ligne ? { ...l, ...maj } : l)));
      setBrouillons((b) => { const n = { ...b }; delete n[ligne.id_ligne]; return n; });
      loadHeader().catch(() => {});
      return true;
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement impossible.');
      return false;
    } finally {
      setEnregistrement((s) => ({ ...s, [ligne.id_ligne]: false }));
    }
  };

  const compter = (ligne) => {
    const brouillon = brouillons[ligne.id_ligne];
    const valeur = brouillon !== undefined ? brouillon : ligne.stock_compte;
    if (valeur === null || valeur === undefined || valeur === '') {
      toast.warn('Saisissez le nombre de paires comptées en rayon.');
      return;
    }
    enregistrer(ligne, { stock_compte: Number(valeur) });
  };

  // --- Groupage par produit ---------------------------------------------------------
  const groupes = useMemo(() => {
    const map = new Map();
    lignes.forEach((l) => {
      if (!map.has(l.id_produit)) {
        map.set(l.id_produit, { id_produit: l.id_produit, nom: l.nom_produit, img: l.img, lignes: [] });
      }
      map.get(l.id_produit).lignes.push(l);
    });
    return [...map.values()];
  }, [lignes]);

  // --- Export CSV (toutes les pages) -------------------------------------------------
  const handleExport = async () => {
    setExporting(true);
    try {
      const all = [];
      let current = 1;
      let total = Infinity;
      while (all.length < total && current <= 60) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await getInventaireLignes(id, { q: search, statut, page: current, page_size: 300 });
        total = data.meta?.total ?? all.length;
        all.push(...(data.data || []));
        if (!data.data?.length) break;
        current += 1;
      }
      const libClassif = (code) => (cfg?.classifications || []).find((c) => c.code === code)?.label || code || '';
      const columns = [
        { title: 'Produit', value: (r) => r.nom_produit },
        { title: 'Pointure', value: (r) => r.taille ?? '' },
        { title: 'Couleur', value: (r) => r.couleur ?? '' },
        { title: 'Code article', value: (r) => r.code_article ?? '' },
        { title: 'Statut ligne', value: (r) => r.statut_ligne },
        { title: 'Stock comptable (photo)', value: (r) => r.stock_theorique ?? '' },
        { title: 'En circulation (photo)', value: (r) => r.en_circulation ?? '' },
        {
          title: 'Théorique rayon',
          value: (r) => (r.stock_theorique === null || r.stock_theorique === undefined
            ? r.theorique_rayon_actuel : r.stock_theorique - r.en_circulation),
        },
        { title: 'Compté', value: (r) => r.stock_compte ?? '' },
        { title: 'Écart', value: (r) => r.ecart ?? '' },
        { title: 'Classification', value: (r) => libClassif(r.classification) },
        { title: 'Commentaire', value: (r) => r.commentaire ?? '' },
        { title: 'Compté le', value: (r) => (r.date_comptage ? new Date(r.date_comptage).toLocaleString('fr-FR') : '') },
      ];
      downloadCsv(`inventaire-${header?.reference || id}.csv`, toCsv(columns, all));
      toast.success(`${all.length} ligne(s) exportée(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  // --- Validation ---------------------------------------------------------------------
  const abandonner = () => {
    let motif = '';
    Modal.confirm({
      title: "Abandonner l'inventaire ?",
      content: (
        <div>
          <p>Aucune correction ne sera appliquée. Les comptages saisis restent consultables.</p>
          <Input placeholder="Motif (optionnel)" maxLength={200} onChange={(e) => { motif = e.target.value; }} />
        </div>
      ),
      okText: 'Abandonner', okButtonProps: { danger: true }, cancelText: 'Retour', centered: true,
      onOk: async () => {
        try {
          await abandonnerInventaire(id, motif);
          toast.success('Inventaire abandonné.');
          await load();
        } catch (e) {
          toast.error(e.response?.data?.error || 'Abandon impossible.');
        }
      },
    });
  };

  if (error && !loading) {
    return <NdoePage><NdoeError text={error} onRetry={load} /></NdoePage>;
  }
  if (loading || !header) {
    return <NdoePage><NdoeLoading rows={6} /></NdoePage>;
  }

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / PAGE_SIZE));

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Stock · Inventaires"
        title={`${header.reference} — ${header.type === 'total' ? 'total' : 'cyclique'}`}
        subtitle={header.commentaire || 'Comptez le rayon ; l\'écart se mesure hors paires en course chez les livreurs.'}
        actions={(
          <>
            {enCours && (
              <NdoeActionButton onClick={() => navigate(`/inventaires/${id}/compter`)}>
                Compter
              </NdoeActionButton>
            )}
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}/equipe`)}>
              Équipes
            </NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}/direct`)}>
              En direct
            </NdoeActionButton>
            <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
              CSV
            </NdoeActionButton>
            {enCours && peutValider && (
              <NdoeActionButton variant="secondary" onClick={abandonner}>Abandonner</NdoeActionButton>
            )}
            {enCours && peutValider && (
              <NdoeActionButton onClick={() => navigate(`/inventaires/${id}/cloturer`)}>
                Clôturer
              </NdoeActionButton>
            )}
          </>
        )}
      />

      <NdoeStats className="pp-stats">
        <NdoeStatCard label="Comptées" value={`${header.lignes_comptees}/${header.lignes_total}`} gold />
        <NdoeStatCard label="Manquants" value={Number(header.paires_manquantes) || 0} />
        <NdoeStatCard label="Surplus" value={Number(header.paires_surplus) || 0} />
        <NdoeStatCard label="À recompter" value={Number(header.a_recompter) || 0} />
        <NdoeStatCard
          label="Manquants (prix de vente)"
          value={Number(header.valeur_manquants_vente) ? money(header.valeur_manquants_vente) : '—'}
        />
        <NdoeStatCard
          label={header.cout_couverture_pct ? `Manquants au coût (${header.cout_couverture_pct} %)` : 'Manquants au coût'}
          value={header.valeur_manquants_cout !== null && header.valeur_manquants_cout !== undefined
            ? money(header.valeur_manquants_cout) : '—'}
        />
      </NdoeStats>

      {header.statut !== 'en_cours' && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone={header.statut === 'valide' ? 'ok' : 'neutral'}
            label={header.statut === 'valide' ? 'Validé' : 'Abandonné'} />
          {' '}Cette session est fermée : le comptage n'est plus modifiable.
          {header.statut === 'valide' && ' Les corrections sont tracées dans le journal (mouvements 14/15).'}
        </p>
      )}
      {enCours && Number(header.a_recompter) > 0 && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone="warn" label="À recompter" />
          {' '}{header.a_recompter} ligne(s) ont bougé entre le comptage et la validation (ventes pendant
          l'inventaire) : recomptez-les puis validez à nouveau. Rien n'a été écrasé.
        </p>
      )}

      <div className="inv-filtres">
        <Input placeholder="Rechercher un produit…" value={q} onChange={(e) => setQ(e.target.value)} allowClear />
        <Select options={LIGNE_STATUTS} value={statut} style={{ minWidth: 170 }}
          onChange={(v) => { setStatut(v); setPage(1); }} />
        <NdoeActionButton size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>🖼 Galerie</NdoeActionButton>
      </div>

      <div className="inv-groupes">
        {groupes.map((g) => (
          <section className="inv-produit ndoe-card" key={g.id_produit}>
            <div className="inv-produit__head">
              <NdoeImage src={g.img} alt={g.nom} size="md" preview={false} />
              <NdoeArticleLink onClick={() => openFiche(g.id_produit)}><b>{g.nom}</b></NdoeArticleLink>
              <small>{g.lignes.length} pointure(s)</small>
            </div>
            <div className="inv-lignes">
              <div className="inv-ligne inv-ligne--head">
                <span>Pointure</span><span>Rayon (théorique)</span><span>Compté</span><span>État</span>
              </div>
              {g.lignes.map((l) => {
                const fige = !enCours || l.statut_ligne === 'validee' || l.statut_ligne === 'sans_ecart';
                const brouillon = brouillons[l.id_ligne];
                const valeur = brouillon !== undefined ? brouillon : (l.stock_compte ?? null);
                const theorique = l.statut_ligne === 'a_compter'
                  ? l.theorique_rayon_actuel
                  : Number(l.stock_theorique) - Number(l.en_circulation);
                const aEcart = l.statut_ligne === 'comptee' && Number(l.ecart) !== 0;
                return (
                  <div className="inv-ligne__bloc" key={l.id_ligne}>
                    <div className="inv-ligne">
                      <span className="inv-taille">
                        <b>P{l.taille ?? '—'}</b>
                        {l.couleur && <small>{l.couleur}</small>}
                      </span>
                      <span className="num inv-theorique">
                        {theorique}
                        {Number(l.statut_ligne === 'a_compter' ? l.circulation_actuelle : l.en_circulation) > 0 && (
                          <small> +{l.statut_ligne === 'a_compter' ? l.circulation_actuelle : l.en_circulation} en course</small>
                        )}
                        {Number(l.avariees) > 0 && (
                          <small className="inv-avarie-note">{l.avariees} avariée(s) à part — ne pas compter</small>
                        )}
                      </span>
                      <span className="inv-saisie">
                        <InputNumber inputMode="decimal"
                          min={0} max={99999} step={1} precision={0}
                          value={valeur}
                          disabled={fige}
                          onChange={(v) => setBrouillons((b) => ({ ...b, [l.id_ligne]: v }))}
                          onPressEnter={() => !fige && compter(l)}
                          placeholder="—"
                        />
                        {!fige && (
                          <NdoeActionButton size="sm" variant="secondary" icon={<CheckOutlined />}
                            loading={!!enregistrement[l.id_ligne]}
                            onClick={() => compter(l)} aria-label="Enregistrer le comptage" />
                        )}
                      </span>
                      <span className="inv-etat">{ligneBadge(l)}</span>
                    </div>
                    {(aEcart || (l.statut_ligne === 'a_recompter' && Number(l.ecart) !== 0)) && (
                      <div className="inv-justif">
                        <Select
                          placeholder="Pourquoi cet écart ?"
                          options={classifications}
                          value={l.classification || undefined}
                          disabled={!enCours}
                          onChange={(v) => enregistrer(l, { classification: v })}
                          style={{ minWidth: 220 }}
                        />
                        <Input
                          placeholder={l.classification === 'autre' ? 'Précisez (obligatoire pour « Autre »)' : 'Commentaire (optionnel)'}
                          defaultValue={l.commentaire || ''}
                          maxLength={255}
                          disabled={!enCours}
                          onBlur={(e) => {
                            const val = e.target.value.trim();
                            if (val !== (l.commentaire || '')) enregistrer(l, { commentaire: val });
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {!groupes.length && (
          <p className="inv-vide">Aucune ligne sur ce filtre. {statut && 'Élargissez le filtre ou la recherche.'}</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="inv-pagination">
          <NdoeActionButton variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Précédent
          </NdoeActionButton>
          <span className="num">page {meta.page} / {totalPages} · {meta.total} lignes</span>
          <NdoeActionButton variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Suivant →
          </NdoeActionButton>
        </div>
      )}

      {ficheElement}
      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} refLists={refs}
        onSelect={(v) => { setQ(v.nom_produit || ''); setPickerOpen(false); }} />

    </NdoePage>
  );
}
